import {
	ChangeDetectionStrategy,
	Component,
	effect,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { TuiButton, TuiLoader } from '@taiga-ui/core';
import { where } from 'firebase/firestore';
import { catchError, of, take } from 'rxjs';
import { AnalysisApiService } from '../../../services';
import { BreakdownSectionOption } from '../breakdown-card.component';

@Component({
	standalone: true,
	selector: 'mar-breakdown-card-button',
	imports: [TuiButton, TranslocoPipe, TuiLoader],
	host: { class: 'contents' },
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrl: 'breakdown-card-button.component.scss',
	template: `
		@if (option(); as option) {
			<!-- TODO: (Taiga UI migration) [appearance] binding uses a dynamic expression. If it can produce "error"/"success"/"glass", replace with "negative"/"positive"/"secondary-grayscale" -->
			<button
				tuiButton
				[appearance]="option.appearance"
				(click)="onSelect()"
				[class.active]="active()"
				[disabled]="loading()"
			>
				<span>{{ option.label | transloco }}</span>
				<span>
					@if (loading()) {
						<tui-loader size="s" />
					} @else {
						{{ count() }}
					}
				</span>
			</button>
		}
	`,
})
export class BreakdownCardButtonComponent {
	readonly analysisApiService = inject(AnalysisApiService);

	readonly option = input.required<BreakdownSectionOption>();
	readonly active = input.required<boolean>();
	readonly entity = input.required<'customers' | 'products'>();
	readonly analysisId = input.required<string>();
	readonly calculationId = input.required<string>();

	readonly count = signal<number>(0);
	readonly loading = signal<boolean>(false);

	readonly marSelect = output<BreakdownSectionOption>();
	readonly marLoaded = output<void>();

	constructor() {
		effect(() => {
			this.loading.set(true);

			const constraints = this.option().queryConstraints.map((q) =>
				where(q.property as string, q.op, q.value),
			);

			this.analysisApiService
				.fetchCollectionCount({
					constraints,
					analysisId: this.analysisId(),
					calculationId: this.calculationId(),
					entity: this.entity(),
				})
				.pipe(
					take(1),
					catchError(() => of(0)),
				)
				.subscribe((value) => {
					this.count.set(value);
					this.loading.set(false);
					this.marLoaded.emit();
				});
		});
	}

	onSelect() {
		this.marSelect.emit(this.option());
	}
}
